import React from 'react';
import { Link } from 'react-router-dom';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faPhone, faToolbox } from '@fortawesome/free-solid-svg-icons';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import './ModernHeroSection.css';

const ModernHeroSection = ({
  badge: propBadge,
  title: propTitle,
  subtitle: propSubtitle,
  actions: propActions,
  stats: propStats,
  additionalText: propAdditionalText,
  imageSrc,
  imageAlt: propImageAlt
}) => {
  const { t } = useTranslation();

  const title = propTitle || t('home.hero.title', 'Mantenimiento Electromecánico de Precisión para la Industria de Dispositivos Médicos');
  const subtitle = propSubtitle || t('home.hero.subtitle', 'Más de 16 años garantizando el cumplimiento normativo, la seguridad y la continuidad operativa de sus instalaciones médicas e industriales en Costa Rica.');
  
  const actions = propActions || [
    {
      key: 'services',
      text: t('home.hero.actions.services.text', "Nuestros Servicios"),
      href: "/servicios",
      variant: "primary",
    },
    {
      key: 'contact',
      text: t('home.hero.actions.contact.text', "Contáctanos"),
      href: "/contacto",
      variant: "outline-primary",
      icon: "phone",
    },
  ];

  const additionalText = propAdditionalText || t('home.hero.additionalText', 'Conectando tecnología y servicio para un futuro más saludable.');
  
  const stats = propStats || [
    { value: t('home.hero.stats.0.value', '16+'), label: t('home.hero.stats.0.label', 'Años de Experiencia') },
    { value: t('home.hero.stats.1.value', '50+'), label: t('home.hero.stats.1.label', 'Clientes Satisfechos') },
    { value: t('home.hero.stats.2.value', '100+'), label: t('home.hero.stats.2.label', 'Proyectos Completados') },
  ];

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (custom) => ({
      opacity: 1,
      y: 0,
      transition: { delay: custom * 0.1, duration: 0.5, ease: "easeOut" }
    })
  };
  
  const imageAlt = propImageAlt || t('home.hero.imageAlt', 'ProMedic Hero Image');

  return (
    <section className="modern-hero-section">
      <div className="background-gradient"></div>
      <Container className="hero-container">
        <Row className="align-items-center">
          <Col lg={7} md={12} className="hero-content">
            <motion.h1 custom={1} initial="hidden" animate="visible" variants={itemVariants}>{title}</motion.h1>
            <motion.p custom={2} initial="hidden" animate="visible" variants={itemVariants} className="lead hero-subtitle">{subtitle}</motion.p>

            {additionalText && (
              <motion.p custom={3} initial="hidden" animate="visible" variants={itemVariants} className="additional-text">{additionalText}</motion.p>
            )}

            <motion.div custom={4} initial="hidden" animate="visible" variants={itemVariants} className="hero-buttons">
              {actions.map((action, index) => (
                <Link key={index} to={action.href}>
                  <Button
                    variant={action.variant}
                    size="lg"
                    className={`hero-button ${action.variant === "outline-primary" ? "btn-outline" : ""}`}
                  >
                    {action.text}
                    {action.icon && (
                      <FontAwesomeIcon
                        icon={action.icon === "phone" ? faPhone : faArrowRight}
                        className="ms-2"
                      />
                    )}
                    {!action.icon && action.variant === "primary" && (
                      <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
                    )}
                  </Button>
                </Link>
              ))}
            </motion.div>

            {stats && stats.length > 0 && (
              <motion.div custom={5} initial="hidden" animate="visible" variants={itemVariants} className="hero-trust-strip">
                {stats.map((stat, index) => (
                  <div className="hero-trust-item" key={index}>
                    <span className="hero-trust-value">{stat.value}</span>
                    <span className="hero-trust-label">{stat.label}</span>
                  </div>
                ))}
              </motion.div>
            )}
          </Col>
          
          <Col lg={5} md={12} className="hero-image-container">
             <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, ease: "easeOut" }} className="hero-image-frame">
              <div className="hero-image">
                {imageSrc ? (
                    <img src={imageSrc} alt={imageAlt} className="img-fluid" />
                ) : (
                    <div className="image-placeholder">
                        <FontAwesomeIcon icon={faToolbox} className="placeholder-icon" />
                        <span className="placeholder-text">PROMEDIC</span>
                    </div>
                )}
              </div>
              <div className="glow-effect"></div>
            </motion.div>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

ModernHeroSection.propTypes = {
  badge: PropTypes.shape({
    text: PropTypes.string,
    action: PropTypes.shape({
      text: PropTypes.string,
      href: PropTypes.string,
    }),
  }),
  title: PropTypes.string,
  subtitle: PropTypes.string,
  actions: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string,
    text: PropTypes.string,
    href: PropTypes.string,
    variant: PropTypes.string,
    icon: PropTypes.string,
  })),
  stats: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
  })),
  additionalText: PropTypes.string,
  imageSrc: PropTypes.string,
  imageAlt: PropTypes.string,
};

export default ModernHeroSection; 